import {Box, Button, Heading, VStack, Text, Icon, HStack, Progress} from "@chakra-ui/react";
import {WarningTwoIcon, RepeatIcon} from "@chakra-ui/icons";
import {useState, useEffect} from "react";
import colors from '../../theme/colors';
import './offlineScreen.scss';

const OfflineScreen = ({onRetry, nextRetryIn, isLoading = false}) => {
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const formatDate = (date) => {
        const daysOfWeek = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
        const dayOfWeek = daysOfWeek[date.getDay()];
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${dayOfWeek} ${day}/${month}/${year}`;
    };

    const formatTime = (date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return { hours, minutes, seconds };
    };

    const FlipDigit = ({ value }) => (
        <Box
            bg="rgba(0, 0, 0, 0.6)"
            borderRadius="md"
            border="2px solid"
            borderColor="gray.600"
            px={{base: 2, md: 3}}
            py={{base: 1, md: 2}}
            mx={0.5}
            position="relative"
            overflow="hidden"
        >
            <Text
                fontSize={{base: "3xl", md: "5xl"}}
                fontWeight="bold"
                color="white"
                fontFamily="monospace"
                lineHeight="1"
            >
                {value}
            </Text>
            <Box
                position="absolute"
                top="50%"
                left="0"
                right="0"
                height="1px"
                bg="gray.700"
            />
        </Box>
    );
    return (
        <Box className="offline-screen">
            <VStack
                spacing={{base: 3, md: 4}}
                align="center"
                justify="center"
                minH="100vh"
                p={{base: 4, md: 6}}
                maxW="600px"
                mx="auto"
                w="100%"
            >
                {/* Data e Ora */}
                {!isLoading && (
                    <VStack spacing={4}>
                        {/* Data */}
                        <Box
                            bg="rgba(0, 0, 0, 0.4)"
                            borderRadius="md"
                            px={{base: 4, md: 6}}
                            py={{base: 2, md: 3}}
                            border="1px solid"
                            borderColor="gray.700"
                        >
                            <Text
                                fontSize={{base: "lg", md: "2xl"}}
                                color="white"
                                fontWeight="bold"
                                textAlign="center"
                                fontFamily="monospace"
                                letterSpacing="wide"
                            >
                                {formatDate(currentTime)}
                            </Text>
                        </Box>

                        {/* Flip Clock */}
                        <HStack spacing={1}>
                            <FlipDigit value={formatTime(currentTime).hours[0]} />
                            <FlipDigit value={formatTime(currentTime).hours[1]} />
                            <Text fontSize={{base: "3xl", md: "5xl"}} color="white" fontWeight="bold" mx={1}>:</Text>
                            <FlipDigit value={formatTime(currentTime).minutes[0]} />
                            <FlipDigit value={formatTime(currentTime).minutes[1]} />
                            <Text fontSize={{base: "3xl", md: "5xl"}} color="white" fontWeight="bold" mx={1}>:</Text>
                            <FlipDigit value={formatTime(currentTime).seconds[0]} />
                            <FlipDigit value={formatTime(currentTime).seconds[1]} />
                        </HStack>
                    </VStack>
                )}

                {/* Logo */}
                <Box className="logo-container">
                    <img src="/images/logo-big.png" alt="Logo" />
                </Box>

                {/* Icona di stato */}
                <Icon as={WarningTwoIcon} boxSize={{base: 12, md: 16}} color={isLoading ? colors.greenLight : "orange.400"}/>

                {/* Messaggi */}
                <VStack spacing={1} px={{base: 2, md: 0}}>
                    <Heading as="h1" size={{base: "md", md: "lg"}} color="white" textAlign="center">
                        {isLoading ? "Caricamento..." : "Sistema Offline"}
                    </Heading>
                </VStack>

                {!isLoading && (
                    <VStack spacing={3} w="100%" maxW="400px" px={{base: 2, md: 0}}>
                        <Button
                            leftIcon={<RepeatIcon/>}
                            bg={colors.greenDark}
                            color="white"
                            size={{base: "md", md: "lg"}}
                            onClick={onRetry}
                            w="100%"
                            _hover={{
                                bg: colors.greenLight
                            }}
                        >
                            Riprova Subito
                        </Button>

                        {nextRetryIn > 0 && (
                            <VStack spacing={2} w="100%">
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.500" textAlign="center" px={2}>
                                    Prossimo tentativo automatico tra {nextRetryIn} secondi
                                </Text>
                                <Progress
                                    value={(30 - nextRetryIn) / 30 * 100}
                                    size="sm"
                                    sx={{
                                        '& > div': {
                                            backgroundColor: colors.greenLight
                                        }
                                    }}
                                    w="100%"
                                    borderRadius="md"
                                />
                            </VStack>
                        )}
                    </VStack>
                )}

                {!isLoading && (
                    <Box
                        mt={{base: 2, md: 4}}
                        p={{base: 2, md: 3}}
                        bg="rgba(255, 255, 255, 0.1)"
                        borderRadius="md"
                        maxW="500px"
                        w="100%"
                        mx={2}
                    >
                        <Heading as="h4" size="sm" color={colors.greenLight} mb={2}>
                            Possibili cause:
                        </Heading>
                        <VStack align="start" spacing={1}>
                            <HStack align="start">
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.300">•</Text>
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.300">
                                    Il server backend è temporaneamente offline
                                </Text>
                            </HStack>
                            <HStack align="start">
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.300">•</Text>
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.300">
                                    Problemi di connessione di rete
                                </Text>
                            </HStack>
                            <HStack align="start">
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.300">•</Text>
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.300">
                                    Il database MongoDB non è raggiungibile
                                </Text>
                            </HStack>
                        </VStack>
                    </Box>
                )}
            </VStack>
        </Box>
    );
};

export default OfflineScreen;
